/* eslint-disable no-unused-vars */
import type { TDocument, TDraft, THeading, TVersion } from '@modules/documents';
import { ApprovalStatus, TApproval, TApprovalPhaseKey } from '@modules/approvals';
import { TFile, TFileVersion } from '@modules/files';

import { TIcon, TMap, TSelectableTableRow, TTableRow } from '@shared/types';
import { TCustomFieldRaw } from '@modules/users';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '@modules/inlineBlocks';
import { TSearchItem } from '@modules/search';
import { EFolderType, TCountryRegistration } from './folders.types';
import { AccessType } from '@modules/access/types';
import type { TPeriodicReview } from './periodicReviews.types';
import { EDashboardReviewState, EDashboardWorkflow } from './itemClassification.types';
import type { TBaseStats } from '@shared/types/stats.types';

export type TStats = TBaseStats & {
  latestVersionId?: string;
  approvedVersionId?: string;
  authors: string[];
};

type TRecentData = {
  viewedDate: string;
  location: string;
};

export type TApprovalData = {
  approvalId: string;
  phaseKey: TApprovalPhaseKey;
  dateOpened: string;
  versionId: string;
  creator: string;
  location: string;
};

export type TItemType = 'FOLDER' | 'DOCUMENT' | 'FILE' | 'INLINE_BLOCK' | 'REPLICA_DOCUMENT' | 'REPLICA_FILE';

export type TTemplateSource = {
  versionId: string;
  fieldValuesBlobName?: string;
};

export type TItem = {
  _id: string;
  name: string;
  stats: TStats;
  type: TItemType;
  path: string[];
  hasDraft: boolean;
  numErrors: number;
  accessRight: AccessType;
  accessRightAllowFrozen: AccessType;
  isBlock: boolean;
  isTemplate: boolean;
  isFrozen: boolean;

  hasDefaultVariant?: boolean;
  folderType?: EFolderType;
  blocksFolder?: string;
  latestMimetype?: string;
  preview?: string;
  recentData?: TRecentData;
  approvalData?: TApprovalData;
  approvalStatusOfLatestVersion?: ApprovalStatus;
  countryRegistration?: TCountryRegistration;
  blockTagId?: string;
  periodicReview?: TPeriodicReview;
  classification?: {
    workflow: EDashboardWorkflow;
    periodicReview: EDashboardReviewState;
    daysUntilReview?: number;
  };
};

export type TItemNameUpdateResult = {
  name?: string;
  pdfExportFileNames?: TMap<string, string>;
};

export type TItemRenameResult = TItemNameUpdateResult & { name: string };

export type TResolvedItem = {
  item: TItem;
  resolvedHeadings: TMap<string, THeading>;
};

export type TResolvedItemsMap = TMap<string, TResolvedItem>;

export type TResolvedItemNames = TMap<string, string>;

export type TSignature = {
  dateSigned: string;
  certificate: string;
};

export type TUniversalItemId = {
  _id: string;
  itemType: TItemType;
};

export enum EBindingMode {
  /** Template structure is locked; only template fields are editable in the created document. */
  BOUND = 'BOUND',
  /** Full HTML copy of the template; the created document is freely editable. */
  DETACHED = 'DETACHED',
}

export type TCopyItem = TUniversalItemId & {
  templateVersionId?: string;
  bindingMode?: EBindingMode;
};

export type TVersionInfo = {
  _id: string;
  type: TItemType;
  name: string;
  versions: TVersion[] | TFileVersion[];
  draft?: TDraft;
  approvals: TApproval[];
};

export type TVersionPreference = 'DRAFT' | 'VERSION' | 'APPROVED';
export type TCopyVersionPreference = TVersionPreference | 'APPROVED_AND_LATEST';

// `type` repeats item.type because TS does not narrow a union by a nested discriminant (item.type).
export type TItemVersion =
  | { type: 'DOCUMENT'; item: TDocument; version: TVersion | TDraft }
  | { type: 'FILE'; item: TFile; version: TFileVersion }
  | { type: 'INLINE_BLOCK'; item: TInlineBlock; version: TInlineBlockVersion | TInlineBlockDraft };

export const isDocumentItemVersion = (
  itemVersion: TItemVersion | undefined
): itemVersion is Extract<TItemVersion, { type: 'DOCUMENT' }> => itemVersion?.type === 'DOCUMENT';
export const isFileItemVersion = (
  itemVersion: TItemVersion | undefined
): itemVersion is Extract<TItemVersion, { type: 'FILE' }> => itemVersion?.type === 'FILE';
export const isInlineBlockItemVersion = (
  itemVersion: TItemVersion | undefined
): itemVersion is Extract<TItemVersion, { type: 'INLINE_BLOCK' }> => itemVersion?.type === 'INLINE_BLOCK';

export const isTDraft = (
  version: TDraft | TVersion | TFileVersion | TInlineBlockVersion | TInlineBlockDraft | undefined
): version is TDraft | TInlineBlockDraft => Boolean(version && !(version as TVersion).id);

export type TCommonVersion = {
  id: string;
};

export type TParsedVersion = {
  major: number;
  minor: number;
  isDraft: boolean;
};

export type TCreateVersionFormData = {
  note: string;
  file?: FileList | null;
};

export type TItemsTableRow = (TSelectableTableRow<TItem | TSearchItem> | TTableRow<TItem | TSearchItem>) & {
  rootName?: string;
  /** Status of an item the user cannot open, e.g. "No access"; the name is not a link, versions and authors are hidden. */
  unavailableLabel?: string;
  approvalDateOpened?: string;
  submissionDueDate?: string;
  openApprovals?: number;
}; // support minimal rows

export type TShowColumn = {
  products?: boolean; // unique for "registrations" table
  authors?: boolean;
  location?: boolean; // unique for "search" table
  status?: boolean;
  date?: boolean;
  archivedBy?: boolean; // unique for "archive" table
  approvedVersion?: boolean;
  preview?: boolean; // unique for "blocks" table
  version?: boolean;
  approvalOpenSince?: boolean;
  reviewDue?: boolean;
  reviewer?: boolean;
  submissionDue?: boolean;
  openApprovals?: boolean;
};

export type TDisableColumn = {
  name?: boolean;
  location?: boolean;
};

export type TColumnLabels = {
  name?: string;
  version?: string;
  reviewDue?: string;
  date?: string;
};

export interface TAction {
  children: string;
  icon: TIcon;
  order: number;
  onClick: (affectedItems: TItem[]) => void | Promise<void>;
  show: (affectedItems: TItem[]) => boolean; // hide/show
  enable: (affectedItems: TItem[]) => boolean; // enable/disable
  getDisabledMessage?: (affectedItems: TItem[]) => string; // if disabled, show this message
}

export type TCustomFieldValue = {
  customField: string; // id of the field
  value: TCustomFieldRaw;
  editor: string; // userId
  editedDate: string; // ISO date
};

/** Safely extract customFieldValues from any version type (not all versions carry them). */
export const getCustomFieldValues = (version: TVersion | TFileVersion | undefined): TCustomFieldValue[] => {
  if (!version || !('customFieldValues' in version)) return [];
  return version.customFieldValues ?? [];
};

export enum EHandlePdfBlobMode {
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
}
